import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/api/auth/auth.service';
import { ModalService } from '@shared/components/modal/modal.service';
import { SessionExpiredModalComponent } from '@shared/components/session-expired-modal/session-expired-modal.component';
import { APP_CONSTANTS } from '@app/configs/app.constants';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class UserSessionService {
    private router = inject(Router);
    private authService = inject(AuthService);
    private modalService = inject(ModalService);

    private destroy$ = new Subject<void>();
    private inactivityTimer: any;
    private currentModalId: number | null = null;
    private channel: BroadcastChannel | null = null;

    // Session state
    readonly isSessionActive = signal<boolean>(true);
    readonly remainingTime = signal<number>(0);
    readonly showWarning = signal<boolean>(false);

    // Configuration from app.constants
    private readonly timeoutMinutes = APP_CONSTANTS.SESSION_TIMEOUT_MINUTES;

    // Events that should reset the inactivity timer
    private readonly ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'wheel'];

    /**
     * Start tracking user inactivity
     */
    startTracking(): void {
        if (!this.authService.user()) {
            return;
        }

        // Initialize BroadcastChannel for cross-tab communication (before stopping)
        if (!this.channel) {
            this.initializeCrossTabSync();
        }

        // Stop any existing tracking (but keep BroadcastChannel)
        this.destroy$.next();
        this.clearTimers();

        // Close warning modal if open
        if (this.currentModalId !== null) {
            this.modalService.close(this.currentModalId);
            this.currentModalId = null;
        }

        this.isSessionActive.set(true);
        this.showWarning.set(false);

        // Set up activity listeners
        this.setupActivityListeners();

        // Start the inactivity timer
        this.resetInactivityTimer();
    }

    /**
     * Stop tracking user inactivity
     */
    stopTracking(): void {
        this.destroy$.next();
        this.clearTimers();
        this.closeBroadcastChannel();

        // Close warning modal if open
        if (this.currentModalId !== null) {
            this.modalService.close(this.currentModalId);
            this.currentModalId = null;
        }

        this.isSessionActive.set(false);
        this.showWarning.set(false);
        this.remainingTime.set(0);
    }

    /**
     * Initialize cross-tab communication
     */
    private initializeCrossTabSync(): void {
        if (typeof BroadcastChannel !== 'undefined') {
            this.channel = new BroadcastChannel('auth_channel');

            // Listen for logout events from other tabs
            this.channel.addEventListener('message', (event) => {
                if (event.data.type === 'LOGOUT') {
                    console.log('Logout triggered from another tab');
                    this.handleLogoutFromOtherTab();
                } else if (event.data.type === 'SESSION_TIMEOUT') {
                    console.log('Session timeout triggered from another tab');
                    this.handleSessionTimeoutFromOtherTab();
                }
            });
        }
    }

    /**
     * Broadcast logout to other tabs
     */
    broadcastLogout(): void {
        if (this.channel) {
            this.channel.postMessage({ type: 'LOGOUT' });
        }
    }

    /**
     * Broadcast session timeout to other tabs
     */
    private broadcastSessionTimeout(): void {
        if (this.channel) {
            this.channel.postMessage({ type: 'SESSION_TIMEOUT' });
        }
    }

    /**
     * Handle logout from another tab
     */
    private handleLogoutFromOtherTab(): void {
        // Clear local user data (don't call API again)
        this.authService.clearUser();
        this.stopTracking();

        // Force page reload to clear all state
        window.location.href = '/login';
    }

    /**
     * Handle session timeout from another tab
     */
    private handleSessionTimeoutFromOtherTab(): void {
        // Clear local user data (don't call API again - already logged by the tab that timed out)
        this.authService.clearUser();
        this.stopTracking();

        // Force page reload to clear all state
        window.location.href = '/login';
    }

    /**
     * Close BroadcastChannel
     */
    private closeBroadcastChannel(): void {
        if (this.channel) {
            this.channel.close();
            this.channel = null;
        }
    }

    /**
     * Set up listeners for user activity events
     */
    private setupActivityListeners(): void {
        const events$ = this.ACTIVITY_EVENTS.map((event) => fromEvent(document, event));

        // Merge all activity events and debounce to avoid excessive resets
        merge(...events$)
            .pipe(
                debounceTime(1000), // Only reset once per second max
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.isSessionActive()) {
                    this.resetInactivityTimer();
                }
            });
    }

    /**
     * Reset the inactivity timer
     */
    private resetInactivityTimer(): void {
        this.clearTimers();
        this.showWarning.set(false);

        const timeoutMs = this.timeoutMinutes * 60 * 1000;
        const expiryTime = new Date(Date.now() + timeoutMs);

        console.log(
            `Session timer reset. Will expire at: ${expiryTime.toLocaleTimeString()} (in ${
                this.timeoutMinutes
            } minutes)`
        );

        // Set expiration timer
        this.inactivityTimer = setTimeout(() => {
            console.log('Session expired due to inactivity');
            this.handleSessionExpiry();
        }, timeoutMs);
    }

    /**
     * Handle session expiry
     */
    private handleSessionExpiry(): void {
        this.isSessionActive.set(false);
        this.showWarning.set(false);
        this.remainingTime.set(0);

        // Auto logout without navigation
        this.performLogout();

        // Show expiry modal
        this.showExpiryModal();
    }

    /**
     * Show session expired modal
     */
    private showExpiryModal(): void {
        this.currentModalId = this.modalService.open({
            title: 'Session Expired',
            contentComponent: SessionExpiredModalComponent,
            size: 'md',
            position: 'center',
            animation: 'fade',
            backdropDismiss: false,
            keyboardClose: false,
            showCloseButton: false,
        });
    }

    /**
     * Perform logout (clear tokens and user data)
     */
    private performLogout(): void {
        // Broadcast session timeout to other tabs
        this.broadcastSessionTimeout();

        // Log session timeout first, then logout (creates both audit entries)
        this.authService.logSessionTimeout().subscribe({
            next: () => {
                console.log('Session timeout logged');
            },
            error: (error) => {
                console.error('Failed to log session timeout:', error);
            },
            complete: () => {
                // Then perform logout (which also logs LOGOUT event)
                this.authService.logout().subscribe({
                    next: () => {
                        console.log('Session expired - user logged out');
                    },
                    error: () => {
                        // Even if logout fails on server, clear local state
                        this.authService.clearUser();
                        console.log('Session expired - local state cleared');
                    },
                });
            },
        });
    }

    /**
     * Clear all timers
     */
    private clearTimers(): void {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
    }
}
